import React, { useCallback, useContext, useMemo } from 'react';
import { ScreenableFieldSetupContext } from '../helpers/useScreenableFieldSetupContextManager';
import { InternalApiEntityGroup } from '../../../api-internal';
import { useQuerySegments } from '../../configure/segments/segmentActions';
import { SegmentsTable } from './SegmentsTable';
import './ExcludedSegmentsStep.scss';
import InternalLink from '../../../common/InternalLink';

export const ExcludedSegmentsStep = () => {
  const { entityType, includedSegments, excludedSegments, updateExcludedSegments } =
    useContext(ScreenableFieldSetupContext);
  const { data: segments, isLoading: isLoading } = useQuerySegments({ entityType });

  const selectedSegments = useMemo(() => {
    return segments?.filter((segment) => segment.code && excludedSegments?.includes(segment.code)) ?? [];
  }, [segments, excludedSegments]);

  const setSelectedSegments = useCallback(
    (segments: InternalApiEntityGroup[]) => {
      updateExcludedSegments(segments.map((it) => it.code!));
    },
    [updateExcludedSegments]
  );

  return (
    <div className='exclusion-groups-page'>
      <div className='top-container'>
        <div className='group-table'>
          <h4 className='text-strong-m chapter-title'>Excluded Segments (optional)</h4>
          <span className='text-s chapter-subtitle'>
            Select Segments to be excluded in this Screening Flow. Excluded Segments are combined using OR logic: an
            entity will be excluded from the Screening Flow if it belongs to any of the selected Segments. If you
            haven’t defined any Segments yet, you can do so <InternalLink title={'here'} link={'/segments'} />.
          </span>
          <SegmentsTable
            segments={segments ?? []}
            isLoading={isLoading}
            selectedSegments={selectedSegments}
            setSelectedSegments={setSelectedSegments}
            disabledCodes={includedSegments ?? []}
          />
        </div>
      </div>
      <div className='exclusion-explanation-container'>
        <div className='title-s'>How does exclusion work?</div>
        <div className='text-s m-b-24'>
          Selected entities would be excluded from the previously made Segment selection
        </div>
        <div className='legend-container'>
          <div className='venn-diagram'>
            <div className='circle left'></div>
            <div className='circle right'></div>
          </div>
          <div className='legend'>
            <ul>
              <li>
                <span className='dot primary' />
                <span className='text'>Selected entities - used for this Screening Flow</span>
              </li>
              <li>
                <span className='dot secondary' />
                <span className='text'>Excluded entities</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
